import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Section } from '@carouselgg/nexus/section'
import { Text } from '@carouselgg/nexus/text'
import { SEO } from '@carouselgg/nexus/seo'
import { Breadcrumb } from '@carouselgg/nexus/breadcrumb'
import { ActionLink } from '../../components/ActionLink'
import { OrgCard } from '../../components/OrgCard'
import { DiscoveryHero, DiscoveryCTA } from '../../components/DiscoveryHero'
import { discoverOrganizations } from '../../api/discover'
import type { DiscoverOrg } from '../../api/discover'
import { AUDIENCES } from './content'
import styles from './AudiencePage.module.css'

/** Turns the `\n` in a headline into real line breaks. */
function headline(title: string) {
  const lines = title.split('\n')
  return lines.map((line, i) => (
    <span key={line}>
      {line}
      {i < lines.length - 1 && <br />}
    </span>
  ))
}

/**
 * `/for/:audience` — the pitch for one kind of organization.
 *
 * One template, three fills. Org type used to be a browsing address, which
 * meant universities got a directory of three empty programs where they needed
 * an argument. Every type now gets the same page and the same depth.
 */
export function AudiencePage() {
  const { audience } = useParams<{ audience: string }>()
  const content = audience ? AUDIENCES[audience] : undefined

  const [orgs, setOrgs] = useState<DiscoverOrg[]>([])
  const orgType = content?.orgType

  useEffect(() => {
    if (!orgType) return
    let cancelled = false
    discoverOrganizations({ type: orgType, limit: 2, offset: 0 })
      .then((res) => { if (!cancelled) setOrgs(res.organizations ?? []) })
      .catch(() => { if (!cancelled) setOrgs([]) })
    return () => { cancelled = true }
  }, [orgType])

  if (!content) return <Navigate to="/orgs" replace />

  return (
    <div className={styles.page}>
      <SEO title={content.seo.title} description={content.seo.description} />

      <Container size="lg">
        {/* These pages are reachable from the /orgs hero, the homepage audience
            cards, the footer and /compete, so a "Back" link would be a guess.
            The directory is the true parent of a type's pitch, and a trail says
            where you are as well as where you came from. */}
        <Breadcrumb
          className={styles.crumbs}
          items={[{ label: 'Organizations', href: '/orgs' }, { label: content.crumb }]}
          renderLink={(crumb, children) => <Link to={crumb.href ?? '/orgs'}>{children}</Link>}
        />

        <DiscoveryHero
          eyebrow={content.eyebrow}
          title={headline(content.title)}
          subtext={content.subtext}
          aside={
            <div className={styles.heroActions}>
              <ActionLink to={content.primaryCta.to}>{content.primaryCta.label}</ActionLink>
              <ActionLink to={content.secondaryCta.to} variant="secondary">
                {content.secondaryCta.label}
              </ActionLink>
            </div>
          }
        />

        {/* Value props */}
        <div className={styles.props}>
          {content.props.map((prop, i) => (
            <div key={prop.title} className={styles.prop}>
              <span className={styles.propNumber} aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h2 className={styles.propTitle}>{prop.title}</h2>
              <p className={styles.propBody}>{prop.body}</p>
            </div>
          ))}
        </div>

        {/* What actually ships, and what does not */}
        <Section spacing="lg">
          <div className={styles.truth}>
            <div className={styles.shipping}>
              <p className={styles.columnLabel}>Shipping today</p>
              <ul className={styles.featureList}>
                {content.shipping.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            {content.building && (
              <div className={styles.building}>
                <p className={`${styles.columnLabel} ${styles.columnLabelMuted}`}>Building next</p>
                <ul className={`${styles.featureList} ${styles.featureListMuted}`}>
                  {content.building.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
                <p className={styles.buildingNote}>{content.building.note}</p>
              </div>
            )}
          </div>
        </Section>

        {/* Who is already here */}
        <Section spacing="lg">
          <div className={styles.listHead}>
            <Text as="h2" size="xl" weight="bold" className={styles.listTitle}>
              {content.directory.heading}
            </Text>
            <span className={styles.spacer} />
            <Link to={`/orgs?type=${content.orgType}`} className={styles.directoryLink}>
              {content.directory.linkLabel} →
            </Link>
          </div>

          <div className={styles.grid}>
            {orgs.map((org) => <OrgCard key={org.id} org={org} />)}
            <div className={styles.placeholder}>
              <span className={styles.placeholderTitle}>{content.directory.emptyTitle}</span>
              <p className={styles.placeholderBody}>{content.directory.emptyBody}</p>
              <Link to={content.primaryCta.to} className={styles.placeholderLink}>
                {content.primaryCta.label} →
              </Link>
            </div>
          </div>
        </Section>

        <DiscoveryCTA
          title={content.closing.title}
          subtext={content.closing.body}
          to={content.closing.to}
          label={content.closing.label}
        />
      </Container>
    </div>
  )
}
